/* eslint-disable @typescript-eslint/quotes */
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Location } from '@angular/common';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, concatMap, filter, map, switchMap, tap, throttleTime } from 'rxjs/operators';
import {
  authGuestLogin,
  authHardLoginSuccess,
  authLogout,
  authUserLoadedGuest,
  authUserLoadedHard,
  authUserLoadedSoft
} from '../../core/auth/auth.actions';
import { AuthLoginState, ServerAuthData } from '../../core/auth/auth.models';
import { selectAuthLoginState } from '../../core/auth/auth.selectors';
import { saveClickLog } from '../../core/clicks/click.actions';
import { ClickLog, ClickOperation } from '../../core/clicks/click.models';
import { LocalStorageService } from '../../core/local-storage/local-storage.service';
import { LogService } from '../../core/log/log.service';
import { showSnackbar } from '../../core/notifications/notification.actions';
import { NotificationService } from '../../core/notifications/notification.service';
import { navigateHome, navigateToPage } from '../../core/router/routes.actions';
import {
  closeDialog,
  updatePassword,
  updatePasswordFailure
} from '../../shared/dialog/dn-dialog.actions';
import { openEmailConfirmDialog } from '../../shared/dialog/mail-confirm-dialog/mail-confirm-dialog.actions';
import {
  updateEmail,
  updateEmailFailure,
  updateEmailSuccess
} from './email-change.actions';
import { getOneAndWaitforLoaded, onlyWhenOnline } from '../../shared/util/operators';
import { ErrorMessage, ErrorType } from '../error/error-message.model';
import { manageError } from '../error/error.util';
import {
  activateUser,
  activateUserFailure,
  loadUser,
  loadUserFailure,
  loadUserGuest,
  loadUserSoft,
  loadUserSoftSuccess,
  loadUserSuccess,
  registerUser,
  registerUserFailure,
  registerUserSuccess,
  resendActivationMail,
  resendActivationMailSuccess,
  retrievePassword,
  retrievePasswordFailure,
  retrievePasswordSuccess,
  saveUserInStore,
  updateUser,
  updateUserFailure,
  updateUserSuccess
} from './user.actions';
import { EmailConfirmStatus, ResendEmailDTO, User, UserDTO } from './user.model';
import { selectUser, selectUserId, selectUserError } from './user.selectors';
import { UserService } from './user.service';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class UserEffects {
  saveUserInStore$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(saveUserInStore),
        tap((action) => this.logService.info('Effect: saveUserInStore ', action)),
        map((payload) => payload.utente),
        tap((user) => {
          this.localStorageService.setItem('USER', { idAnagrafica: user.idAnagrafica });
        })
      ),
    { dispatch: false }
  );

  setLogServiceUser$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadUserSuccess, loadUserSoftSuccess, loadUserGuest),
        map((payload) => (payload.utente ? payload.utente.idAnagrafica : -1)),
        tap((idAnagrafica) => {
          this.logService.idAnagrafica = idAnagrafica;
        })
      ),
    { dispatch: false }
  );

  activateUser$ = createEffect(() => {
    let params: ParamMap;
    let oldSessionID: number;
    let url: string;
    let referrer: string
    return this.actions$.pipe(
      ofType(activateUser),
      tap((action) => this.logService.info('Effect: activateUser ', action)),
      tap((action) => {
        params = action.params;
        oldSessionID = action.oldSessionID;
        url = action.url;
        referrer = action.referrer;
      }),
      onlyWhenOnline(this.store),
      switchMap(({ token, callbackUrl }) =>
        this.userService.activateUser(token).pipe(
          tap((serverAuthData: ServerAuthData) => {
            this.logService.infoDebug('Effect: activateUser data received --> ', serverAuthData);
          }),

          concatMap((serverAuthData: ServerAuthData) => [
            authHardLoginSuccess({ serverAuthData: serverAuthData, params: params, oldSessionID: oldSessionID, url: url, referrer: referrer }),
            // navigateHome(),
            showSnackbar({
              payload: this.notificationService.getOkNotification(
                0,
                'Utente confermato con successo!'
              )
            })
          ]),
          catchError((error: ErrorMessage) => of(activateUserFailure({ error: error, callbackUrl: callbackUrl })))
        )
      )
    )
  }
  );

  loadUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUser),
      tap((action) => this.logService.info('Effect: loadUser ', action)),

      onlyWhenOnline(this.store),
      getOneAndWaitforLoaded(this.store),
      switchMap((action) =>
        this.userService.loadUser(action.idAnagrafica).pipe(
          tap((data) => this.logService.infoDebug('Effect: loadUser data received --> ', data)),

          map((user) => loadUserSuccess({ utente: user })),
          catchError((error: ErrorMessage) => of(loadUserFailure({ error: error })))
        )
      )
    )
  );

  loadUserSoft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserSoft),
      tap((action) => this.logService.info('Effect: loadUserSoft', action)),
      onlyWhenOnline(this.store),
      getOneAndWaitforLoaded(this.store),
      switchMap((action) =>
        this.userService.loadUser(action.idAnagrafica).pipe(
          tap((data) => this.logService.infoDebug('Effect: loadUserSoft data received --> ', data)),
          map((user) => {
            if (user.noDottnet == true) {
              // se l'utente si è cancellato da dottnet non può più accedere non eseguo soft login ma setto la login guest
              return authGuestLogin()

            } else {
              return loadUserSoftSuccess({ utente: user });
            }
          }),
          catchError((error: ErrorMessage) => of(loadUserFailure({ error: error })))
        )
      )
    )
  );

  loadUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserSuccess),
      tap((action) => {
        this.logService.info('Effect: loadUserSuccess ', action);
      }),
      map((payload) => payload.utente),
      tap((user) => {
        // set user local storage used by drclick_soa
        this.localStorageService.setItem('USER', { idAnagrafica: user.idAnagrafica });
      }),
      map(() => authUserLoadedHard())
    )
  );

  loadUserSoftSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserSoftSuccess),
      tap((action) => {
        this.logService.info('Effect: loadUserSoftSuccess', action);
      }),
      map(() => authUserLoadedSoft())
    )
  );

  // new-login (1.10): il guest raggiunge LOADED (authProgress fedele) solo DOPO
  // il caricamento dell'utente guest. loadUserGuest è l'evento "guest caricato"
  // (nessun load async, dati guest sincroni), quindi qui emettiamo
  // authUserLoadedGuest — come loadUserSuccess$→authUserLoadedHard. Sostituisce
  // il dispatch manuale (e a rovescio) in app.component.
  loadUserGuestLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserGuest),
      map(() => authUserLoadedGuest())
    )
  );

  registerUser$ = createEffect(() => {
    let savedUser: User;
    const clickLog: ClickLog = <ClickLog>{};
    let params: ParamMap;
    let oldSessionID: number;
    let url: string;
    let referrer: string
    return this.actions$.pipe(
      ofType(registerUser),
      tap((action) => this.logService.info('Effect: registerUser', action)),
      tap((action) => {
        savedUser = action.newUser
        params = action.params;
        oldSessionID = action.oldSessionID;
        url = action.url;
        referrer = action.referrer;
      }),
      switchMap(({ newUser, queryParams }) =>
        this.userService.registerUser({ user: newUser, callbackUrl: queryParams.callbackUrl }).pipe(
          tap((serverAuthData: ServerAuthData) => {
            clickLog.clickLogOperationID = ClickOperation.Registrazione;
            clickLog.content = JSON.stringify(savedUser);
            clickLog.message = 'Registrazione OK';
            clickLog.method = 'POST';
            clickLog.outcome = true;
            this.logService.infoDebug('Effect: registerUser data received --> ', serverAuthData);

          }),
          concatMap((serverAuthData: ServerAuthData) => [
            authHardLoginSuccess({ serverAuthData: serverAuthData, params: params, oldSessionID: oldSessionID, url: url, referrer: referrer }),
            saveClickLog({ clickLog }),
            navigateToPage({ page: 'profile/activate', queryParams }),
            showSnackbar({
              payload: this.notificationService.getOkNotification(
                0,
                'Utente registrato con successo. Conferma il tuo account attraverso il collegamento inviato alla tua mail usata in fase di registrazione'
              )
            })
          ]),
          catchError((error: ErrorMessage) => {
            clickLog.clickLogOperationID = ClickOperation.Registrazione;
            clickLog.content = JSON.stringify(savedUser);
            clickLog.message = error.messageDN;
            clickLog.method = 'POST';
            clickLog.outcome = false;
            this.store.dispatch(saveClickLog({ clickLog }));

            return of(registerUserFailure({ error }));
          })
        )
      )
    );
  });

  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUser),
      tap((action) => this.logService.info('Effect: updateUser ', action)),
      switchMap(({ newUser, callbackUrl }) =>
        this.userService.updateUser(newUser, callbackUrl).pipe(
          tap((returnedUser: UserDTO) =>
            this.logService.infoDebug('Effect: updateUser data received --> ', returnedUser)
          ),
          map((returnedUser: UserDTO) => updateUserSuccess({ newUser: returnedUser })),
          catchError((error: ErrorMessage) => of(updateUserFailure({ error })))
        )
      )
    )
  );

  updateUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUserSuccess),
      tap((action) => this.logService.info('Effect: updateUserSuccess ', action)),
      concatMap(({ newUser }) => {
        if (newUser.emailConfirmationStatus !== EmailConfirmStatus.ConfirmedEmail) {
          return [
            saveUserInStore({ utente: newUser }),
            openEmailConfirmDialog({ callbackUrl: this.location.path(true) })
          ];
        }
        return [saveUserInStore({ utente: newUser })];
      })
    )
  );

  resendActivationMail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resendActivationMail),
      throttleTime(5000),
      tap((action) => this.logService.info('Effect: resendActivationMail ', action)),
      concatLatestFrom(() => [
        this.store.select(selectUser),
        this.store.select(selectUserError),
        this.store.select(selectAuthLoginState)
      ]),

      switchMap(([{ callbackUrl }, user, userError, authLoginState]) => {
        if (!this.areUserInfoValid(user, userError)) {
          throw new Error("Can't retrieve user infos to send an activation mail");
        } else {
          // if we are getting values from userError, this means that user is not valid, an so user.email doesn't exist. So the question mark

          return this.userService
            .resendActivationMail(<ResendEmailDTO>{
              email: user?.email || userError.hint.split('|')[0].split(':')[1],
              userId: user?.idAnagrafica || +userError.hint.split('|')[1].split(':')[1],
              callbackUrl: callbackUrl
            }
            )
            .pipe(
              tap((response) =>
                this.logService.infoDebug('Effect: resendActivationMailSuccess ', response)
              ),
              map((response) => resendActivationMailSuccess()),
              catchError((error) => of(manageError(error, authLoginState)))
            );
        }
      })
    )
  );

  resendActivationMailSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resendActivationMailSuccess),
      tap((action) => this.logService.info('Effect: resendActivationMailSuccess ', action)),
      map(() =>
        showSnackbar({
          payload: this.notificationService.getOkNotification(
            0,
            this.ts.instant('dottnet.message.activationMailSent')
          )
        })
      )
    )
  );

  retrievePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(retrievePassword),
      onlyWhenOnline(this.store),
      tap((action) => this.logService.info('Effect: retrievePassword ', action)),

      switchMap(({ email }) =>
        this.userService.retrievePassword(email).pipe(
          tap((response) => this.logService.info('Effect: retrievePassword data --> ', response)),
          map(() => retrievePasswordSuccess()),
          catchError((error: ErrorMessage) => of(retrievePasswordFailure({ error })))
        )
      )
    )
  );

  retrievePasswordSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(retrievePasswordSuccess),
      tap((action) => this.logService.info('Effect: retrievePasswordSuccess ', action)),
      map(() =>
        showSnackbar({
          payload: this.notificationService.getOkNotification(
            0,
            this.ts.instant('dottnet.message.retrievepassword')
          )
        })
      )
    )
  );

  retrievePasswordFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(retrievePasswordFailure),
      tap((action) => this.logService.info('Effect: retrievePasswordFailure', action)),
      map((action) => action.error),
      map((error) =>
        showSnackbar({
          payload: this.notificationService.getNotificationFromError(error)
        })
      )
    )
  );

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updatePassword),
      onlyWhenOnline(this.store),
      // map((payload) => payload.email),
      switchMap((payload) =>
        this.userService.changePassword(payload.passwordChange).pipe(
          concatMap(() => [
            showSnackbar({
              payload: this.notificationService.getOkNotification(
                0,
                this.ts.instant('dottnet.message.changedpassword')
              )
            }),
            closeDialog()
          ]),
          catchError((error: ErrorMessage) => of(updatePasswordFailure({ error })))
        )
      )
    )
  );

  changePasswordFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updatePasswordFailure),
      tap((action) => this.logService.info('Effect: changePasswordFailure', action)),
      map((action) => action.error),
      map((error) =>
        showSnackbar({
          payload: this.notificationService.getNotificationFromError(error)
        })
      )
    )
  );

  updateEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateEmail),
      onlyWhenOnline(this.store),
      concatLatestFrom(() => this.store.select(selectUserId)),
      switchMap(([payload, userId]) =>
        this.userService.checkMailExistenceInternal(payload.newEmail, userId ?? -1).pipe(
          switchMap((checkResult) => {
            if (checkResult.idEsiste !== 0) {
              const error = new ErrorMessage(this.ts.instant('dottnet.field.error.mailExistence'));
              error.hint = 'mailExistence';
              return of(updateEmailFailure({ error }));
            }
            return this.userService.updateEmail(payload.newEmail, payload.callbackUrl).pipe(
              concatMap((updatedUser) => [
                updateEmailSuccess({ updatedUser }),
                saveUserInStore({ utente: updatedUser }),
                showSnackbar({
                  payload: this.notificationService.getOkNotification(
                    0,
                    this.ts.instant('dottnet.email.update.success')
                  )
                }),
                openEmailConfirmDialog({ callbackUrl: this.location.path(true) })
              ]),
              catchError((error: ErrorMessage) => of(updateEmailFailure({ error })))
            );
          }),
          catchError((error: ErrorMessage) => of(updateEmailFailure({ error })))
        )
      )
    )
  );

  updateEmailFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateEmailFailure),
      tap((action) => this.logService.info('Effect: updateEmailFailure', action)),
      filter((action) => action.error?.hint !== 'mailExistence'),
      map((action) => action.error),
      map((error) =>
        showSnackbar({
          payload: this.notificationService.getNotificationFromError(error)
        })
      )
    )
  );

  operationOnUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUserSuccess, registerUserSuccess),
      tap((action) => this.logService.info('Effect: operationOnUserSuccess', action)),
      concatMap(() => [
        showSnackbar({
          payload: this.notificationService.getOkNotification()
        })
      ])
    )
  );

  loadUserFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserFailure),
      tap((action) => this.logService.info('Effect: loadUserFailure ', action)),
      concatLatestFrom(() => this.store.select(selectAuthLoginState)),
      map(([{ error }, authLoginState]) => manageError(error, authLoginState))
    )
  );

  activateUserFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(activateUserFailure),
      tap(({ error }) => this.logService.info('Effect: activateUserFailure ', error)),
      concatMap(({ error, callbackUrl }) => [
        openEmailConfirmDialog({ callbackUrl, emailConfirmationStatus: EmailConfirmStatus.LinkExpired }),
        resendActivationMail({ callbackUrl })
      ])
    )
  );

  operationOnUserFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUserFailure, registerUserFailure),
      tap((action) => this.logService.info('Effect: operationOnUserFailure* ', action)),
      map((action) => action.error),
      map((error) =>
        showSnackbar({
          payload: this.notificationService.getNotificationFromError(error)
        })
      )
    )
  );

  constructor(
    private actions$: Actions,
    private logService: LogService,
    private ts: TranslateService,
    private router: Router,
    public actRoute: ActivatedRoute,
    private userService: UserService,
    private location: Location,
    private notificationService: NotificationService,
    private localStorageService: LocalStorageService,
    private store: Store
  ) { }

  // Checks if user has an email + id || user data coming from BE error
  // if jwt from softlogin is expired BE returns mail:current email|idAnagrafica:currentIdAdnagrafica
  // jwtsoftlogin.go riga 198
  // why? Don't know
  private areUserInfoValid(user: UserDTO, userError: ErrorMessage): boolean {
    if (userError) {
      // Split the inital string over pipe symbol and lower the case + trim
      const splittedHint = userError?.hint
        ?.split('|')
        ?.map((elem) => elem.toLowerCase())
        ?.map((elem) => elem.trim());
      // Is the hint wrote in a known pattern
      const isHintPresent =
        splittedHint?.[0]?.includes('mail') && splittedHint?.[1]?.includes('idanagrafica');
      // Are the hint values actually there
      const isHintOk = !!splittedHint?.[0]?.split(':')?.[1] && !!splittedHint?.[1]?.split(':')?.[1];

      return isHintPresent && isHintOk;
    } else return !!user?.email && !!user?.idAnagrafica;
  }
}
